/**
 * FirmwareCompliance — Customer > Monitor > Firmware status (formerly Firmware Compliance). What is running vs what was approved.
 *
 * WHY THIS SCREEN MATTERS MORE THAN IT LOOKS: on this fleet firmware drift is not cosmetic —
 * it is upstream of the certificate problem. Measured across 19 devices there were SEVEN
 * distinct builds, and the six devices whose certificates had expired were exactly the six on
 * the old builds. Firmware generation maps 1:1 onto PKI generation, so "off baseline" and
 * "about to become unmanageable" are the same list.
 *
 * ⚠ IT NEVER SAYS "BEHIND". Three vendors use three version schemes with no common ordering
 * (`Edgecore 12.5.93` / `r23485+110` / `v4.1.2_rc4`). Claiming one is older would require
 * comparing them, and a wrong comparison sends an upgrade to the wrong build. The states are
 * matches / differs / no baseline / unknown, and the copy is careful to say "differs from the
 * approved build", never "out of date".
 *
 * A baseline IS the approved image for a device type (FirmwareImage.is_stable). Where no image
 * is approved the screen says so plainly rather than implying compliance — on a fleet with no
 * approvals at all, "no baseline" everywhere is the finding.
 *
 * Design: existing Meridian tokens/components only. The status ramp is reserved for operational
 * state, and drift IS one: `warn` for differs, neutral for no-baseline (a gap in configuration,
 * not a device fault).
 */
import { useSession } from "@/stores/session";
import { useFirmwareCompliance } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import type { FirmwareComplianceDevice, FirmwareComplianceType } from "@/types/domain";

const STATE_COPY: Record<FirmwareComplianceDevice["state"], string> = {
  matches: "Matches approved",
  differs: "Differs from approved",
  no_baseline: "No approved image",
  unknown: "Never reported",
};

export default function FirmwareCompliance() {
  const siteId = useSession((s) => s.currentSiteId);
  const siteName = useSession((s) => s.currentSite()?.name);
  const { data, isLoading, error } = useFirmwareCompliance(siteId);

  return (
    <div>
      <PageHeader
        title="Firmware status"
        subtitle={
          siteName
            ? `What is running in ${siteName} versus the approved build`
            : "What is running versus the approved build for each device type"
        }
      />

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Comparing against approvals…</div>}

      {data && data.totals.devices === 0 && (
        <EmptyState title="No devices in scope" hint="Adopt a device and it will appear here." />
      )}

      {data && data.totals.devices > 0 && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Stat label="Devices" value={String(data.totals.devices)} />
            <Stat label="Distinct builds" value={String(data.totals.distinct_builds)}
                  tone={data.totals.distinct_builds > 1 ? "warn" : undefined} />
            <Stat label="Matches" value={String(data.totals.matches)} />
            <Stat label="Differs" value={String(data.totals.differs)}
                  tone={data.totals.differs > 0 ? "warn" : undefined} />
            <Stat label="No baseline" value={String(data.totals.no_baseline)} />
          </div>

          {data.totals.no_baseline === data.totals.devices && (
            /* The state this fleet is actually in. Saying "compliant" here would be a lie by
               omission: nothing has been approved, so there is nothing to comply with. */
            <p className="mb-4 max-w-3xl rounded border border-line bg-surface-2 px-3 py-2 text-sm text-ink-muted">
              No firmware image has been approved for any device type yet, so nothing can be
              measured as compliant. Approve an image under <span className="text-ink">Firmware</span>{" "}
              to set the baseline for its device type.
            </p>
          )}

          <h2 className="mb-2 text-sm font-medium text-ink">By device type</h2>
          <div className="mb-6 flex flex-col gap-2">
            {/* OP#1596: the server sends one entry per (type, baseline) -- two customers can
                approve different builds for one type -- so the key carries both. */}
            {data.by_device_type.map((t) => (
              <TypeCard key={`${t.device_type}|${t.baseline ?? ""}`} t={t} />
            ))}
          </div>

          <h2 className="mb-2 text-sm font-medium text-ink">Devices</h2>
          <div className="md-card overflow-x-auto p-0">
            <table className="w-full text-left text-xs">
              <thead className="text-ink-subtle">
                <tr className="border-b border-line">
                  <th className="px-3 py-2 font-normal">Device</th>
                  <th className="px-3 py-2 font-normal">Type</th>
                  <th className="px-3 py-2 font-normal">Running</th>
                  <th className="px-3 py-2 font-normal">Approved</th>
                  <th className="px-3 py-2 font-normal">State</th>
                </tr>
              </thead>
              <tbody>
                {data.devices.map((d) => (
                  <tr key={d.serial}
                      className={`border-b border-line/60 ${d.state === "differs" ? "bg-warn/5" : ""}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-ink">{d.name}</div>
                      <div className="font-mono text-[0.6875rem] text-ink-subtle">{d.serial}</div>
                    </td>
                    <td className="px-3 py-2 text-ink-muted">{d.device_type}</td>
                    <td className="px-3 py-2 font-mono text-[0.6875rem] text-ink-muted"
                        title={d.running ?? undefined}>
                      {d.running ?? "—"}
                    </td>
                    <td className="px-3 py-2 font-mono text-[0.6875rem] text-ink-muted"
                        title={d.baseline ?? undefined}>
                      {d.baseline ?? "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          d.state === "differs"
                            ? "rounded bg-warn/15 px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-warn"
                            : "text-ink-subtle"
                        }
                      >
                        {STATE_COPY[d.state]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "warn" }) {
  return (
    <div className="md-card px-3 py-2">
      <div className="text-[0.625rem] uppercase tracking-wide text-ink-subtle">{label}</div>
      <div className={`text-lg font-semibold ${tone === "warn" ? "text-warn" : "text-ink"}`}>
        {value}
      </div>
    </div>
  );
}

function TypeCard({ t }: { t: FirmwareComplianceType }) {
  const drifting = t.distinct_builds > 1;
  return (
    <div className="md-card p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <span className="font-medium text-ink">{t.device_type}</span>
          <span className="text-xs text-ink-subtle">{t.total} device(s)</span>
          {drifting && (
            <span className="rounded bg-warn/15 px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-warn">
              {t.distinct_builds} builds
            </span>
          )}
        </div>
        <span className="font-mono text-[0.6875rem] text-ink-subtle">
          {t.baseline ? `approved: ${t.baseline}` : "no approved image"}
        </span>
      </div>
      {drifting && (
        /* The per-build breakdown is the actionable part: it names which builds are in play. */
        <ul className="mt-2 flex flex-col gap-0.5">
          {t.builds.map((b) => (
            <li key={b.version} className="font-mono text-[0.6875rem] text-ink-muted">
              <span className="inline-block w-8 tabular-nums text-ink">{b.count}×</span>
              {b.version}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
